/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Alert, AlertDescription } from "../ui/alert";
import { Checkbox } from "../ui/checkbox";

const RegistrationForm = ({ onBackToHome, onSuccessfulRegister }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    studentId: '',
    email: '',
    pin: '',
    pinConfirm: '',
    shopClass: '',
    shopClasses: []
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [cohorts, setCohorts] = useState([]);
  const [pinLength, setPinLength] = useState(4); // Default, will be fetched from config

  useEffect(() => {
    fetchCohorts();
    fetchPinConfig();
  }, []);

  const fetchPinConfig = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/config/public`);
      if (response.ok) {
        const data = await response.json();
        if (data.config?.security?.min_pin_length) {
          setPinLength(data.config.security.min_pin_length);
        }
      }
    } catch (err) {
      console.warn('Failed to fetch PIN config, using default:', err);
    }
  };

  const fetchCohorts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cohorts`);
      if (!response.ok) throw new Error('Failed to fetch cohorts');
      const data = await response.json();
      setCohorts(data);
    } catch (err) {
      console.error('Error fetching cohorts:', err);
      setError('Failed to load classes. Please try again later.');
    }
  };

  const validatePin = (pin) => {
    const regex = new RegExp(`^\\d{${pinLength}}$`);
    return regex.test(pin);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'pin' || name === 'pinConfirm') {
      const numericValue = value.replace(/\D/g, '').slice(0, pinLength);
      setFormData(prevState => ({
        ...prevState,
        [name]: numericValue
      }));
    } else {
      setFormData(prevState => ({
        ...prevState,
        [name]: value
      }));
    }
  };

  const handleMultipleClassChange = (cohortName, checked) => {
    setFormData(prevState => ({
      ...prevState,
      shopClasses: checked 
        ? [...prevState.shopClasses, cohortName]
        : prevState.shopClasses.filter(name => name !== cohortName)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    console.log('Form data being sent: ', formData);

    if (!formData.shopClass && formData.shopClasses.length === 0) {
      setError('Please select at least one class.');
      return;
    }

    if (!validatePin(formData.pin)) {
      setError(`Please enter a valid ${pinLength}-digit PIN.`);
      return;
    }

    if (formData.pin !== formData.pinConfirm) {
      setError('PINs do not match.');
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      console.log('Response status: ', response.status);
      const data = await response.json();
      console.log('Full registration response:', JSON.stringify(data, null, 2));

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      console.log('Registration successful:', JSON.stringify(data, null, 2));
      setSuccess(true);
      setTimeout(() => {
        onSuccessfulRegister();
      }, 2000); // Redirect after 2 seconds
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
      <Card className="w-full max-w-md mx-auto m-2 sm:m-4">
        <CardHeader className="p-4 sm:p-6">
        <CardTitle className="text-xl sm:text-2xl">Register</CardTitle>
        <CardDescription className="text-sm sm:text-base">Create a new account to access the Safety Testing App</CardDescription>
      </CardHeader>
      <CardContent className="p-4 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              name="firstName"
              type="text"
              required
              value={formData.firstName}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              name="lastName"
              type="text"
              required
              value={formData.lastName}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="studentId">Student ID</Label>
            <Input
              id="studentId"
              name="studentId"
              type="text"
              required
              value={formData.studentId}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              placeholder="student@school.edu"
            />
            <p className="text-xs text-gray-500 mt-1">
              Required for secure PIN reset functionality
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="pin">PIN ({pinLength} digits)</Label>
            <Input
              id="pin"
              name="pin"
              type="password"
              required
              pattern={`\\d{${pinLength}}`}
              inputMode="numeric"
              maxLength={pinLength}
              value={formData.pin}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pinConfirm">Confirm PIN</Label>
            <Input
              id="pinConfirm"
              name="pinConfirm"
              type="password"
              required
              pattern={`\\d{${pinLength}}`}
              inputMode="numeric"
              maxLength={pinLength}
              value={formData.pinConfirm}
              onChange={handleInputChange}
            />
          </div>
          <div className="space-y-2">
            <Label>Classes</Label>
            <p className="text-sm text-gray-600">Select all classes you are enrolled in:</p>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto border rounded-md p-3">
              {cohorts.map((cohort) => (
                <div key={cohort.name} className="flex items-center space-x-2 p-1 hover:bg-gray-50">
                  <Checkbox
                    id={`cohort-${cohort.name}`}
                    checked={formData.shopClasses.includes(cohort.name)}
                    onCheckedChange={(checked) => handleMultipleClassChange(cohort.name, checked)}
                  />
                  <Label htmlFor={`cohort-${cohort.name}`} className="cursor-pointer">
                    {cohort.name}
                  </Label>
                </div>
              ))}
            </div>
            {formData.shopClasses.length > 0 && (
              <p className="text-sm text-green-600">
                Selected: {formData.shopClasses.join(', ')}
              </p>
            )}
          </div>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert>
              <AlertDescription>Registration successful! Redirecting to home...</AlertDescription>
            </Alert>
          )}
          <Button type="submit" className="w-full">Register</Button>
          <Button type="button" variant="outline" className="w-full" onClick={onBackToHome}>
            Back to Home
          </Button>
        </form>
      </CardContent>
    </Card>
    </div>
  );
};

export default RegistrationForm;